import { Button, Form, Input, Modal, Space, Table } from "antd";
import { useDocumentTags } from "../hooks/useDocumentTags";
import { DocumentTagForm } from "./document-tag-form";
import { FontFa } from "../../../../../components";
import { useDeleteDocumentTag } from "../hooks/useDeleteDocumentTag";
import { useQueryClient } from "@tanstack/react-query";
import { CreateNotification } from "../../../../../components/notifier";
import { useState } from "react";
import { useUpdateDocumentTag } from "../hooks/useUpdateDocumentTag";

type Tag = {
  id: string;
  Title: string;
  eTagType: string;
};

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: any;
  inputType: "text";
  record: any;
  index: number;
  children: React.ReactNode;
}

const EditableCell: React.FC<EditableCellProps> = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: true,
              message: `Vui lòng nhập ${title}!`,
            },
          ]}
        >
          <Input />
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

export const DocumentTagTable = () => {
  const { isLoading, tagTypeTableData, eTagTypeOptions, getDataByTagType } = useDocumentTags();
  const { mutate, isLoading: isDeleteLoading } = useDeleteDocumentTag();
  const queryClient = useQueryClient();
  const [editingKey, setEditingKey] = useState("");
  const [form] = Form.useForm();
  const { mutate: updateMutate, isLoading: isUpdateLoading } = useUpdateDocumentTag();

  const isEditing = (record: any) => record.key === editingKey;

  const deleteAllTag = (tagType: string) => {
    const allTags = getDataByTagType(tagType);
    allTags?.forEach((e: Tag) => {
      mutate(e.id, {
        onSuccess() {
          queryClient.invalidateQueries({ queryKey: ["document-tags"] });
        },
      });
    });
    CreateNotification({ title: "Thành công", body: "Xoá loại chủ đề thành công", icon: "/icons/success.png" });
  };

  const handleDeleteDocumentTagType = (tagType: string) => {
    Modal.confirm({
      title: "Thông báo",
      content: "Anh/chị vui lòng xác nhận xoá các dữ liệu đã chọn!",
      okText: "Xác nhận",
      okType: "danger",
      okButtonProps: { type: "primary" },
      cancelText: "Huỷ",
      onOk: () => {
        deleteAllTag(tagType);
      },
    });
  };

  const edit = (record: Partial<any>) => {
    form.setFieldsValue({ name: "", age: "", address: "", ...record });
    setEditingKey(record.key);
  };

  const save = (tagType: string) => {
    const newTagType = form.getFieldValue("tagType");
    const allTags = getDataByTagType(tagType);
    const fields = {
      eTagType: newTagType,
    };
    allTags?.forEach((e: Tag) => {
      updateMutate(
        { id: e.id, body: fields },
        {
          onSuccess() {
            queryClient.invalidateQueries({ queryKey: ["document-tags"] });
          },
        },
      );
    });
    CreateNotification({ title: "Thành công", body: "Cập nhật loại chủ đề thành công", icon: "/icons/success.png" });
  };

  const columns = [
    {
      title: "Danh mục",
      dataIndex: "tagType",
      key: "tagType",
      filters: eTagTypeOptions,
      onFilter: (status: any, record: any) => record.tagType.indexOf(status) === 0,
      width: 300,
      editable: true,
    },
    {
      title: "Chủ đề",
      dataIndex: "tags",
      key: "tags",
      render: (tags: Tag[], record: any) => (
        <DocumentTagForm initialValue={{ tags: [...tags], tagType: record.tagType }} />
      ),
      editable: false,
    },
    {
      dataIndex: "action",
      render: (_: any, record: any) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button
              icon={<FontFa name="faSave" style={{ cursor: "pointer", color: "#26EC5F" }} />}
              type="text"
              onClick={() => save(record.tagType)}
              disabled={isUpdateLoading}
            />
            <Button
              icon={<FontFa name="faTimes" style={{ cursor: "pointer", color: "#F64E6E" }} />}
              type="text"
              onClick={() => setEditingKey("")}
              size="large"
            />
          </Space>
        ) : (
          <Space>
            <Button
              icon={<FontFa name="faEdit" style={{ cursor: "pointer", color: "#34e5eb" }} />}
              type="text"
              onClick={() => edit(record)}
              size="large"
            />
            <Button
              icon={<FontFa name="faTrash" style={{ cursor: "pointer", color: "#F64E6E" }} />}
              type="text"
              size="large"
              onClick={() => handleDeleteDocumentTagType(record.tagType)}
              disabled={isDeleteLoading}
            />
          </Space>
        );
      },
      editable: false,
      align: "center" as const,
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: any) => ({
        record,
        inputType: "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <Form form={form} component={false}>
      <Table
        components={{
          body: {
            cell: EditableCell,
          },
        }}
        columns={mergedColumns}
        bordered
        dataSource={tagTypeTableData}
        loading={isLoading}
      />
    </Form>
  );
};
